import { useMemo } from 'react';
import { isIngredientCompatibleWithPreparation } from './preparationCompatibility';
import {
  buildSynergyIndex,
  rankSynergySuggestions,
  TIP_NAME_CAP,
  TIP_HIDE_AT_SELECTION_COUNT,
} from './synergyGraph';

/**
 * In-memory synergy ranking for the custom-blend ingredient picker.
 * Suggestions stay aligned with the active preparation method.
 */
export function useSynergySuggestions(
  ingredients,
  selectedIngredients,
  displayGroups,
  preparationMethod,
) {
  const prepCompatible = useMemo(
    () =>
      ingredients.filter((ing) =>
        isIngredientCompatibleWithPreparation(ing, preparationMethod),
      ),
    [ingredients, preparationMethod],
  );

  const synergyIndex = useMemo(
    () => buildSynergyIndex(prepCompatible),
    [prepCompatible],
  );

  const eligibleIdSet = useMemo(
    () => new Set(prepCompatible.map((ing) => ing.id)),
    [prepCompatible],
  );

  const rankedSuggestions = useMemo(
    () =>
      rankSynergySuggestions({
        selectedIds: selectedIngredients,
        index: synergyIndex,
        eligibleIdSet,
      }),
    [selectedIngredients, synergyIndex, eligibleIdSet],
  );

  const suggestedFamilyKeys = useMemo(
    () => new Set(rankedSuggestions.map((s) => s.familyKey)),
    [rankedSuggestions],
  );

  const tipSuggestions = useMemo(
    () => rankedSuggestions.slice(0, TIP_NAME_CAP),
    [rankedSuggestions],
  );

  const showSynergyTip =
    selectedIngredients.length > 0 &&
    selectedIngredients.length < TIP_HIDE_AT_SELECTION_COUNT &&
    tipSuggestions.length > 0;

  const singleSelectedName = useMemo(() => {
    if (selectedIngredients.length !== 1) return null;
    const ing = prepCompatible.find((i) => i.id === selectedIngredients[0]);
    if (!ing) return null;
    const familyKey = ing.variant_family || ing.id;
    return synergyIndex.displayNameByFamily.get(familyKey) || ing.name;
  }, [selectedIngredients, prepCompatible, synergyIndex]);

  const groupKeyByFamily = useMemo(() => {
    const map = new Map();
    for (const group of displayGroups) {
      for (const family of group.ingredients) {
        map.set(family.familyKey, group.key);
      }
    }
    return map;
  }, [displayGroups]);

  const groupsWithSynergy = useMemo(() => {
    const keys = new Set();
    for (const familyKey of suggestedFamilyKeys) {
      const gk = groupKeyByFamily.get(familyKey);
      if (gk) keys.add(gk);
    }
    return keys;
  }, [suggestedFamilyKeys, groupKeyByFamily]);

  return {
    tipSuggestions,
    suggestedFamilyKeys,
    showSynergyTip,
    singleSelectedName,
    groupKeyByFamily,
    groupsWithSynergy,
  };
}
